import {
  APPLE_SERVICES_ID,
  FACEBOOK_APP_ID,
  GOOGLE_OAUTH2_CLIENT_ID,
} from "@config";

/**
 * The browser half of SSO: each provider's JS SDK, reduced to the one credential
 * `/api/public/users/sso_sessions` needs to verify the caller.
 *
 * Google and Facebook yield an OAuth **access token**, which the endpoint replays
 * against the provider's userinfo URL (`Mobile::SocialAuth`). Apple has no such
 * URL: it yields an **identity JWT** (`id_token`) that the endpoint verifies
 * against Apple's keys and the web Services ID (`Mobile::NativeAuth`, `os: web`).
 */

export type TSsoProvider = "google" | "facebook" | "apple";

const PROVIDER_CLIENT_IDS: Record<TSsoProvider, string | undefined> = {
  google: GOOGLE_OAUTH2_CLIENT_ID,
  facebook: FACEBOOK_APP_ID,
  apple: APPLE_SERVICES_ID,
};

/** Without a client id the popup could only fail, so the button is not offered. */
export const isSsoProviderConfigured = (provider: TSsoProvider): boolean =>
  Boolean(PROVIDER_CLIENT_IDS[provider]);

const GOOGLE_SDK_SRC = "https://accounts.google.com/gsi/client";
const FACEBOOK_SDK_SRC = "https://connect.facebook.net/en_US/sdk.js";
const APPLE_SDK_SRC =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

/** Pinned to what `Mobile::SocialAuth` calls — v17 expired on 2025-09-12. */
const FACEBOOK_GRAPH_VERSION = "v26.0";

/** Apple has no userinfo endpoint; its identity travels inside the id_token. */
const PROFILE_URLS: Partial<Record<TSsoProvider, string>> = {
  google: "https://www.googleapis.com/oauth2/v2/userinfo",
  facebook: `https://graph.facebook.com/${FACEBOOK_GRAPH_VERSION}/me?fields=email,first_name,last_name`,
};

export interface ISsoProfile {
  email?: string;
  firstName?: string;
  lastName?: string;
}

/**
 * What a provider's popup returns, in the shape the session endpoint reads:
 * an access token for Google/Facebook, an id_token for Apple. `profile` carries
 * the name Apple hands over once on first authorization (Google/Facebook read it
 * back from userinfo instead, so they leave it unset).
 */
export interface ISsoCredential {
  token?: string;
  idToken?: string;
  profile?: ISsoProfile;
}

/**
 * Closing the provider's popup. Not a failure: the visitor is returned to the
 * screen with nothing said, the way the mobile app treats a dismissed sheet.
 */
export class SsoCancelledError extends Error {
  constructor() {
    super("SSO cancelled");
    this.name = "SsoCancelledError";
  }
}

export const isSsoCancelled = (error: unknown): boolean =>
  error instanceof SsoCancelledError;

const scriptPromises = new Map<string, Promise<void>>();

const loadScript = (src: string): Promise<void> => {
  const cached = scriptPromises.get(src);
  if (cached) return cached;

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Dropped so a later attempt can retry rather than reuse the rejection.
      scriptPromises.delete(src);
      reject(new Error(`Failed to load ${src}`));
    };
    document.head.appendChild(script);
  });

  scriptPromises.set(src, promise);

  return promise;
};

interface IGoogleTokenResponse {
  access_token?: string;
}

interface IGoogleIdentityServices {
  accounts: {
    oauth2: {
      initTokenClient: (config: {
        client_id: string;
        scope: string;
        callback: (response: IGoogleTokenResponse) => void;
        error_callback?: (error: unknown) => void;
      }) => { requestAccessToken: () => void };
    };
  };
}

interface IFacebookLoginResponse {
  authResponse?: { accessToken?: string };
}

interface IFacebookSdk {
  init: (config: {
    appId: string;
    cookie: boolean;
    xfbml: boolean;
    version: string;
  }) => void;
  login: (
    callback: (response: IFacebookLoginResponse) => void,
    options: { scope: string }
  ) => void;
}

interface IAppleSignInResponse {
  authorization: { id_token?: string; code?: string; state?: string };
  user?: { name?: { firstName?: string; lastName?: string }; email?: string };
}

interface IAppleAuth {
  init: (config: {
    clientId: string;
    scope: string;
    redirectURI: string;
    usePopup: boolean;
  }) => void;
  signIn: () => Promise<IAppleSignInResponse>;
}

interface ISsoWindow extends Window {
  google?: IGoogleIdentityServices;
  FB?: IFacebookSdk;
  fbAsyncInit?: () => void;
  AppleID?: { auth: IAppleAuth };
}

const ssoWindow = (): ISsoWindow => window as unknown as ISsoWindow;

const loadGoogleSdk = async (): Promise<IGoogleIdentityServices> => {
  await loadScript(GOOGLE_SDK_SRC);

  const google = ssoWindow().google;
  if (!google?.accounts?.oauth2) throw new Error("Google SDK unavailable");

  return google;
};

let facebookSdkPromise: Promise<IFacebookSdk> | undefined;

const loadFacebookSdk = (): Promise<IFacebookSdk> => {
  if (facebookSdkPromise) return facebookSdkPromise;

  facebookSdkPromise = new Promise<IFacebookSdk>((resolve, reject) => {
    // The SDK calls this itself once it is ready, and it is the only signal
    // that `FB` is initialised — so it is set before the script is injected.
    ssoWindow().fbAsyncInit = () => {
      const facebook = ssoWindow().FB;
      if (!facebook) {
        reject(new Error("Facebook SDK unavailable"));

        return;
      }

      facebook.init({
        appId: FACEBOOK_APP_ID as string,
        cookie: false,
        xfbml: false,
        version: FACEBOOK_GRAPH_VERSION,
      });
      resolve(facebook);
    };

    loadScript(FACEBOOK_SDK_SRC).catch(reject);
  }).catch((error: Error) => {
    facebookSdkPromise = undefined;

    throw error;
  });

  return facebookSdkPromise;
};

let appleSdkPromise: Promise<IAppleAuth> | undefined;

const loadAppleSdk = (): Promise<IAppleAuth> => {
  if (appleSdkPromise) return appleSdkPromise;

  appleSdkPromise = loadScript(APPLE_SDK_SRC)
    .then(() => {
      const appleId = ssoWindow().AppleID;
      if (!appleId?.auth) throw new Error("Apple SDK unavailable");

      appleId.auth.init({
        clientId: APPLE_SERVICES_ID,
        scope: "name email",
        redirectURI: `${window.location.origin}/auth/apple/callback`,
        usePopup: true,
      });

      return appleId.auth;
    })
    .catch((error: Error) => {
      appleSdkPromise = undefined;

      throw error;
    });

  return appleSdkPromise;
};

/**
 * Fetched while the visitor is still on the screen, because the SDKs must open
 * their popup from the click itself: awaiting a script inside the handler breaks
 * the user gesture and the browser blocks the window.
 */
export const preloadSsoSdks = () => {
  // Swallowed: a provider whose SDK never arrives is reported when its button
  // is pressed, not before anyone has asked for it.
  const ignore = () => undefined;

  if (isSsoProviderConfigured("google")) void loadGoogleSdk().catch(ignore);
  if (isSsoProviderConfigured("facebook")) void loadFacebookSdk().catch(ignore);
  if (isSsoProviderConfigured("apple")) void loadAppleSdk().catch(ignore);
};

const signInWithGoogle = async (): Promise<string> => {
  const google = await loadGoogleSdk();

  return new Promise<string>((resolve, reject) => {
    google.accounts.oauth2
      .initTokenClient({
        client_id: GOOGLE_OAUTH2_CLIENT_ID as string,
        scope: "email profile",
        callback: (response) => {
          if (!response.access_token) {
            reject(new SsoCancelledError());

            return;
          }

          resolve(response.access_token);
        },
        // Covers a closed popup and one the browser refused to open alike.
        error_callback: () => reject(new SsoCancelledError()),
      })
      .requestAccessToken();
  });
};

const signInWithFacebook = async (): Promise<string> => {
  const facebook = await loadFacebookSdk();

  return new Promise<string>((resolve, reject) => {
    facebook.login(
      (response) => {
        const token = response.authResponse?.accessToken;
        if (!token) {
          reject(new SsoCancelledError());

          return;
        }

        resolve(token);
      },
      { scope: "public_profile,email" }
    );
  });
};

// The SDK rejects with one of these `error` codes when the visitor backs out.
const isAppleCancelled = (error: unknown): boolean => {
  const code = (error as { error?: string } | null)?.error;

  return code === "popup_closed_by_user" || code === "user_cancelled_authorize";
};

const signInWithApple = async (): Promise<ISsoCredential> => {
  const auth = await loadAppleSdk();

  let response: IAppleSignInResponse;
  try {
    response = await auth.signIn();
  } catch (error) {
    if (isAppleCancelled(error)) throw new SsoCancelledError();

    throw error;
  }

  const idToken = response.authorization?.id_token;
  // A resolved sign-in with no token is a failure, not a dismissal: reporting it
  // as cancelled would return the visitor to the screen with nothing said.
  if (!idToken) throw new Error("Apple returned no identity token");

  return {
    idToken,
    // Apple sends the name only on the first authorization; later logins omit it.
    profile: {
      email: response.user?.email,
      firstName: response.user?.name?.firstName,
      lastName: response.user?.name?.lastName,
    },
  };
};

/** The provider's credential, or `SsoCancelledError` if nobody signed in. */
export const signInWithProvider = async (
  provider: TSsoProvider
): Promise<ISsoCredential> => {
  if (provider === "apple") return signInWithApple();

  const token =
    provider === "google"
      ? await signInWithGoogle()
      : await signInWithFacebook();

  return { token };
};

/**
 * The same identity the endpoint reads server-side, asked for again here only
 * when it refuses for a name it was not given: it is what fills the missing-info
 * form, so a failure to read it costs a prefill and nothing more. Apple has no
 * userinfo URL, so its name comes from the sign-in response instead.
 */
export const fetchSsoProfile = async (
  provider: TSsoProvider,
  accessToken: string
): Promise<ISsoProfile> => {
  const url = PROFILE_URLS[provider];
  if (!url) return {};

  try {
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!response.ok) return {};

    const data: {
      email?: string;
      given_name?: string;
      family_name?: string;
      first_name?: string;
      last_name?: string;
    } = await response.json();

    return {
      email: data.email,
      firstName: data.given_name ?? data.first_name,
      lastName: data.family_name ?? data.last_name,
    };
  } catch {
    return {};
  }
};
